import React, {createContext, useEffect, useReducer} from 'react'
import {getAuth} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  or,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore";
import {db} from "../config/firebase";
import {toast} from "react-toastify";
import {chunkArray} from "../utils/functions";
import {LicenseRequestType} from "../utils/enums/LicenseRequestType";


const initialState = {
  workName: "Uylsses",
  royaltyTemplates: [],
  licenseTemplates: [],
  portfolioItems: [],
  licenses: [],
  notifications: [],
  userDetails: null,
  publicWalletAddress: null,
  updatePublicAddress: () => {},
  getUserDetails: async () => {},
  updateUserDetails: async () => {},
}

export const AppContext = createContext(initialState);
const auth = getAuth()

const reducer = (state, action) => {
  switch (action.type) {
    case 'update_royalty_templates':
      return {...state, royaltyTemplates: action.payload}
    case 'update_portfolio_items':
      return {...state, portfolioItems: action.payload}
    case 'update_license_templates':
      return {...state, licenseTemplates: action.payload}
    case 'update_licenses':
      return {...state, licenses: action.payload}
    case 'update_notifications':
      return {...state, notifications: action.payload}
    case 'update_user':
      return {...state, userDetails: {...state.userDetails, ...action.payload}}
    case 'update_public_address':
      return {...state, publicWalletAddress: action.payload}
    default:
      return state;
  }
};

export const AppContextProvider = props => {
  const [state, dispatch] = useReducer(reducer, initialState);


  useEffect(() => {
    if (state?.userDetails?.uid) {
      const requestCollection = collection(db, "user_licenses");
      const customQuery = query(requestCollection, where("buyerUid", "==", state?.userDetails?.uid))

      const unsubscribe = onSnapshot(customQuery, (querySnapshot) => {
        let newData = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          newData.push(data);
        });
        newData = newData.sort((a, b) => b.createdAt - a.createdAt);
        console.log("User Licenses", newData);
        dispatch({type: "update_licenses", payload: [...newData]});
      });

      const fetchNotifications = () => {
        let unsubscribe = null;
        try {
          const userDetails = state.userDetails;
          const notificationsQuery = query(
            collection(db, "license_requests"),
            where("recipientMail", "==", userDetails?.email),
            orderBy("createdAt", "desc")
          );
          unsubscribe = onSnapshot(notificationsQuery, (querySnapshot) => {
            let notifications = [];
            querySnapshot.forEach(doc => {
              const data = doc.data();
              notifications.push(data);
            });
            dispatch({type: "update_notifications", payload: [...notifications]});
          });
        } catch (error) {
          console.log(error);
        }
        return unsubscribe;
      }
      const unsubscribeNotifications = fetchNotifications();

      return () => {
        unsubscribe();
        if (unsubscribeNotifications) {
          unsubscribeNotifications();
        }
      }
    }
  }, [state?.userDetails])

  useEffect(() => {
    if (state?.userDetails?.uid) {
      const userCollectionsCollection = collection(db, "royalty_templates");
      let customQuery = query(userCollectionsCollection, where("userUid", "==", auth.currentUser.uid));
      const unsubscribe = onSnapshot(customQuery, (querySnapshot) => {
        let newData = [];
        querySnapshot.forEach(doc => {
          const data = doc.data();
          newData.push(data);
        });
        newData = newData.sort((a, b) => b.createdAt - a.createdAt);
        console.log("User Royalty Templates", newData);
        dispatch({type: "update_royalty_templates", payload: [...newData]});
      });

      return () => unsubscribe();
    }
  }, [state?.userDetails]);

  useEffect(() => {
    const fetchLicenseTemplates = async () => {
      const userCollectionsCollection = collection(db, "royalty_templates");
      let licensesUid = state?.licenses?.map(license => license?.nftUid) || [];
      const notificationsUid = state?.notifications?.filter(n => n?.type === "SEND_LICENCE" && n?.state === LicenseRequestType.ACCEPTED)?.map(n => n?.nftUid) || [];
      console?.log("Notifications", state?.notifications || [])
      const uids = [...new Set([...licensesUid, ...notificationsUid])];
      const chunks = chunkArray(uids, 10);

      const queryPromises = chunks.map(chunk => {
        const q = query(userCollectionsCollection,
          or(
            where("uid", "in", chunk)
          )
        );
        return getDocs(q);
      });
      let allResults = [];
      const snapshots = await Promise.all(queryPromises);
      snapshots.forEach(snapshot => {
        snapshot.forEach(doc => allResults.push(doc.data()));
      });
      const notificationLicenseUids = state?.notifications?.map(license => license?.licenseUid)
      const userLicenseUids = state?.licenses?.map(license => license?.licenseUid);
      const licenseUids = [... new Set([...notificationLicenseUids, ...userLicenseUids])];
      const results = allResults?.map(item => {

        return {
          ...item,
          licenses: item?.licenses?.filter(i => licenseUids?.includes(i?.uid))
            ?.map(license => {
              const userLicense = state?.licenses?.find(i => i?.licenseUid === license?.uid);
              const notificationLicense = state?.notifications?.find(i => i?.licenseUid === license?.uid);
              console.log(userLicense)
              console.log(license)
              console.log(notificationLicense)
              return {
                ...(userLicense || {}),
                request: {...notificationLicense || {}},
                ...license,
                userLicense: userLicense?.uid
              }
            })
        }
      })?.sort((a, b) => b.createdAt - a.createdAt)
      console.log("All Results", results)

      dispatch({type: "update_license_templates", payload: [...results]});
    }

    if ((state?.licenses && state?.licenses?.length) || (state?.notifications && state?.notifications?.length)) {
      fetchLicenseTemplates().then(() => console.log("Fetch license finished"))
    }
    return () => {
      dispatch({type: "update_license_templates", payload: []});
    }
  }, [state?.licenses, state?.notifications])


  const getUserDetails = async () => {
    if (auth?.currentUser?.uid) {
      try {
        const docRef = doc(db, "users", auth?.currentUser?.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap?.exists()) {
          const userDetails = {...docSnap?.data()}
          if (userDetails?.firstName || userDetails?.lastName) {
            userDetails.displayName = `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`?.trim();
          }
          dispatch({type: "update_user", payload: userDetails});
        } else {
          const userDetails = {
            uid: auth?.currentUser?.uid,
            firstName: "",
            lastName: "",
            displayName: auth.currentUser?.displayName,
            acceptTerms: false,
            email: auth?.currentUser?.email,
            authUid: auth?.currentUser?.uid,
            photoURL: auth?.currentUser?.photoURL,
          }
          await setDoc(doc(db, "users", auth.currentUser.uid), userDetails);
          dispatch({type: "update_user", payload: userDetails});
        }
      } catch (err) {
        console.log(err);
        //TODO: handle error
        // toast.error(err.message);
      }
    }
  }

  const updateUserDetails = async (data) => {
    if (data) {
      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(docRef, {...data});
        dispatch({type: "update_user", payload: {...data}});
      } catch (err) {
        console.log(err);
        toast.error(err.message);
      }
    }
  }
  const updatePublicAddress = async (address = null) => {
    dispatch({type: "update_public_address", payload: address});
  }


  return (
    <AppContext.Provider value={{state, dispatch, getUserDetails, updateUserDetails, updatePublicAddress}}>
      {props.children}
    </AppContext.Provider>
  )
}
